import type { RefObject } from 'react';
import { SearchSelectField } from '@/components/ui/SearchSelectField/SearchSelectField';
import { api } from '@/api/client';
import { contractorDisplayName } from '@/utils/contractorDisplayName';
import { useCatalogManageOpener, useCanManageCatalog } from '@/contexts/CatalogManageOpenerContext';
import './ContractorCombo.css';

const PAGE_SIZE = 50;
const MIN_SEARCH_LENGTH = 2;

export type ContractorValueItem = { id: string; name: string };

interface ContractorComboProps {
  value: ContractorValueItem | null;
  onChange: (contractor: ContractorValueItem | null) => void;
  /**
   * `studio` — поиск по `/studios`.
   * Иначе (по умолчанию) — справочник юрлиц `/legal-entities`.
   * @deprecated `solo` трактуется как юрлица (типов контрагентов больше нет).
   */
  contractorType?: 'studio' | 'solo';
  /** Явный источник: legalEntity | studio (приоритетнее contractorType). */
  source?: 'legalEntity' | 'studio';
  placeholder?: string;
  disabled?: boolean;
  containerRef?: RefObject<HTMLDivElement | null>;
  fullWidth?: boolean;
}

export function ContractorCombo({
  value,
  onChange,
  contractorType,
  source,
  placeholder,
  disabled,
  containerRef,
  fullWidth,
}: ContractorComboProps) {
  const openCatalogManage = useCatalogManageOpener();
  const canManageCatalog = useCanManageCatalog();
  const resolvedSource: 'legalEntity' | 'studio' =
    source ?? (contractorType === 'studio' ? 'studio' : 'legalEntity');
  const resolvedPlaceholder =
    placeholder ?? (resolvedSource === 'studio' ? 'Студия' : 'Юрлицо');
  const typeKey = resolvedSource;

  return (
    <div className={fullWidth ? 'contractor-combo contractor-combo_full-width' : 'contractor-combo'}>
      <SearchSelectField<ContractorValueItem>
        value={value}
        onChange={onChange}
        getOptionLabel={(c) => c.name}
        placeholder={resolvedPlaceholder}
        disabled={disabled}
        containerRef={containerRef}
        queryKey={['contractors', 'search-field', typeKey]}
        minSearchLength={MIN_SEARCH_LENGTH}
        fetchPage={async (debouncedQuery, page) => {
          if (resolvedSource === 'studio') {
            const res = await api.studios.list(debouncedQuery, {
              limit: PAGE_SIZE,
              offset: page * PAGE_SIZE,
            });
            const items = res.items.map((s) => ({
              id: s.id,
              name: s.name,
            }));
            return { items, hasMore: res.hasMore };
          }
          const res = await api.legalEntities.list(debouncedQuery, {
            limit: PAGE_SIZE,
            offset: page * PAGE_SIZE,
          });
          const items = res.items.map((le) => ({
            id: le.id,
            name: contractorDisplayName({ legal_entity: le.name, name: le.name }),
          }));
          return { items, hasMore: res.hasMore };
        }}
        computeAddons={({ debouncedQuery, filteredItems }) => {
          if (!canManageCatalog) return [];
          const q = debouncedQuery.trim();
          if (!q || filteredItems.length > 0) return [];
          if (resolvedSource === 'studio') {
            return [
              {
                id: '__create_studio',
                label: 'Создать студию',
                onSelect: () => {
                  openCatalogManage({
                    tab: 'studios',
                    nameSeed: q,
                    onStudioCreated: (studio) => {
                      onChange({ id: studio.id, name: studio.name });
                    },
                  });
                },
              },
            ];
          }
          return [
            {
              id: '__create_legal_entity',
              label: 'Создать юрлицо',
              onSelect: () => {
                openCatalogManage({
                  tab: 'legal_entities',
                  nameSeed: q,
                  onLegalEntityCreated: (entity) => {
                    onChange({
                      id: entity.id,
                      name: contractorDisplayName({ legal_entity: entity.name, name: entity.name }),
                    });
                  },
                });
              },
            },
          ];
        }}
      />
    </div>
  );
}
